import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLanguageSync } from "../hooks/useLanguageNavigation";
import { LanguageCode, LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from "../utils/languageRouting";

interface LanguageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  usePrefix?: boolean;
}

// Flag emoji mapping for each language
const LANGUAGE_FLAGS: Record<LanguageCode, string> = {
  en: "🇬🇧", // United Kingdom
  it: "🇮🇹", // Italy
  es: "🇪🇸", // Spain
  fr: "🇫🇷", // France
  de: "🇩🇪", // Germany
  pt: "🇵🇹", // Portugal
  ru: "🇷🇺", // Russia
  zh: "🇨🇳", // China
  ja: "🇯🇵", // Japan
  ar: "🇸🇦", // Saudi Arabia
  hi: "🇮🇳", // India
  ko: "🇰🇷", // South Korea
  nl: "🇳🇱", // Netherlands
  cs: "🇨🇿", // Czech Republic
};

/**
 * Language Selection Modal
 * Full-screen modal with flags and language names in a 2-column grid
 */
const LanguageSelectionModal: React.FC<LanguageSelectionModalProps> = ({
  isOpen,
  onClose,
  usePrefix = true,
}) => {
  const { changeLanguage, currentLanguage } = useLanguageSync();

  const handleLanguageSelect = async (language: LanguageCode) => {
    await changeLanguage(language, { usePrefix });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-xl sm:rounded-2xl w-full max-w-[95vw] sm:max-w-md md:max-w-lg lg:max-w-2xl p-4 sm:p-6 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="mb-4 sm:mb-6 flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              Select Language
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
            </button>
          </div>

          {/* Languages Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6 overflow-y-auto pr-1 sm:pr-2 flex-1">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang}
                onClick={() => handleLanguageSelect(lang)}
                className={`
                  flex items-center gap-3 p-3 sm:p-4 rounded-lg border-2 transition-all
                  ${currentLanguage === lang
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                  }
                `}
              >
                <span className="text-2xl sm:text-3xl">{LANGUAGE_FLAGS[lang]}</span>
                <span className={`font-medium text-sm sm:text-base ${
                  currentLanguage === lang ? "text-blue-600" : "text-gray-700"
                }`}>
                  {LANGUAGE_NAMES[lang]}
                </span>
              </button>
            ))}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full py-2.5 sm:py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors text-sm sm:text-base"
          >
            Close
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LanguageSelectionModal;

